import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Menu, Transition } from '@headlessui/react';
import { Question, Assessment, Response } from '../types';
import { questionService } from '../services/questionService';
import { assessmentService } from '../services/assessmentService';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';
import QuestionDelegation from '../components/QuestionDelegation';
import QuestionnaireOverview from '../components/QuestionnaireOverview';
import ClientGoalsSection from '../components/ClientGoalsSection';
import QuestionnaireErrorBoundary from '../components/QuestionnaireErrorBoundary';
import { useLayout, getDensityClasses } from '../contexts/LayoutContext';
import { useHeader } from '../contexts/HeaderContext';
import { ArrowLeftIcon, ChevronDownIcon, ChevronUpIcon, CheckCircleIcon, XCircleIcon, ExclamationTriangleIcon, DocumentTextIcon, FlagIcon, ChatBubbleLeftRightIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { getScaleLabels, getMatchingScaleOption } from '../utils/scaleLabels';
import { getQuestionOptionsList, getQuestionOptionsAsLegacyShape, getScoringRulesMap } from '../utils/questionUtils';
import toast from 'react-hot-toast';

// Helper to parse comma-joined multiselect responses robustly even if options contain commas
export const getSelectedMultiselectValues = (defaultValue: string | null | undefined, options: string[]): string[] => {
  if (!defaultValue) return [];
  
  // Sort options by length descending to match larger options first (avoids substring issues)
  const sortedOptions = [...options].sort((a, b) => b.length - a.length);
  
  const selected: string[] = [];
  let remaining = defaultValue;
  
  for (const option of sortedOptions) {
    if (remaining.includes(option)) {
      selected.push(option);
      remaining = remaining.replace(option, '');
    }
  }
  
  return options.filter(opt => selected.includes(opt));
};


// Helper to calculate automatic/default score of an answer value on the client-side
const getAutomaticScore = (question: Question, answerValue: string | null | undefined): number => {
  if (!answerValue) return 0;
  switch (question.questionType) {
    case 'binary':
      return answerValue === 'Yes' ? 4 : 0;
    case 'scale': {
      const scaleMap: Record<string, number> = {
        'Never': 0,
        'Between Never & Sometimes': 1,
        'Sometimes': 2,
        'Between Sometimes & Always': 3,
        'Always': 4
      };
      return scaleMap[answerValue] || 0;
    }
    case 'multiple_choice': {
      const rulesMap = getScoringRulesMap(question);
      return rulesMap[answerValue] ?? 0;
    }
    case 'multiselect': {
      const optionsList = getQuestionOptionsList(question);
      const selectedOptions = getSelectedMultiselectValues(answerValue, optionsList);
      const totalOptions = question.options?.length ?? 0;
      return totalOptions > 0 ? Math.round((selectedOptions.length / totalOptions) * 4 * 100) / 100 : 0;
    }
    case 'text':
    default:
      return 0;
  }
};

// Reusable Manual Score Slider component with dynamic min/max calculations
const ManualScoreSlider: React.FC<{
  question: Question;
  response: Response;
  assessment: Assessment | null;
  onResponsesUpdate: () => Promise<void>;
}> = ({ question, response, assessment, onResponsesUpdate }) => {
  // Enforce notes eligibility check (exempt for text questions)
  const isText = question.questionType === 'text';
  const hasNotes = response.notes && response.notes.trim() !== '';
  if (!isText && !hasNotes) {
    return null;
  }

  // Get dynamic min/max
  const rulesMap = getScoringRulesMap(question);
  const scores = Object.values(rulesMap);
  let min = 0;
  let max = 4;
  if (scores.length > 0) {
    min = Math.min(...scores);
    max = Math.max(...scores);
    if (min === max) {
      if (max === 0) {
        min = 0;
        max = 4;
      } else {
        min = 0;
      }
    }
  }

  const getScoreValue = (score: number | string | null | undefined): number => {
    const value = score ?? min;
    const numValue = typeof value === 'string' ? parseFloat(value) : Number(value);
    return isNaN(numValue) ? min : numValue;
  };

  const currentScore = getScoreValue(response.score);
  const [localScore, setLocalScore] = useState<number>(currentScore);
  const [isUpdatingScore, setIsUpdatingScore] = useState(false);

  useEffect(() => {
    const score = getScoreValue(response.score);
    setLocalScore(score);
  }, [response.score]);

  const handleScoreChange = async (newScore: number) => {
    setIsUpdatingScore(true);
    try {
      await assessmentService.updateTextQuestionScore(response.id, newScore);
      await onResponsesUpdate();
      toast.success('Score updated');
    } catch (error: any) {
      console.error('Failed to update score:', error);
      toast.error(error.message || 'Failed to update score');
      setLocalScore(getScoreValue(response.score));
    } finally {
      setIsUpdatingScore(false);
    }
  };

  const isReadOnly = assessment?.status === 'COMPLETED' || assessment?.status === 'ARCHIVED';

  return (
    <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
      <div className="flex items-center justify-between mb-2">
        <label className="text-sm font-medium text-gray-700">
          Manual Score ({min.toFixed(1)} - {max.toFixed(1)})
        </label>
        <span className="text-sm font-mono text-gray-600">
          {localScore.toFixed(1)} / {max.toFixed(1)}
        </span>
      </div>
      <div className="space-y-2">
        <input
          type="range"
          min={min}
          max={max}
          step="0.1"
          value={localScore}
          onChange={(e) => {
            const val = parseFloat(e.target.value);
            setLocalScore(val);
          }}
          onMouseUp={(e) => {
            const val = parseFloat((e.target as HTMLInputElement).value);
            handleScoreChange(val);
          }}
          onTouchEnd={(e) => {
            const val = parseFloat((e.target as HTMLInputElement).value);
            handleScoreChange(val);
          }}
          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={isUpdatingScore || isReadOnly}
        />
        <div className="flex justify-between text-xs text-gray-500 font-mono mt-1">
          <span>{min.toFixed(1)} (Min)</span>
          <span>{max.toFixed(1)} (Max)</span>
        </div>
      </div>
      <button
        type="button"
        onClick={() => handleScoreChange(min)}
        className="mt-2 text-xs text-gray-600 hover:text-gray-800 underline disabled:opacity-50 disabled:cursor-not-allowed"
        disabled={isUpdatingScore || isReadOnly}
      >
        Reset to {min}
      </button>
    </div>
  );
};


// Component for text questions with score slider
const TextQuestionWithScore: React.FC<{
  question: Question;
  response?: Response;
  assessment: Assessment | null;
  defaultValue: string;
  onTextChange: (value: string) => Promise<void>;
  canScore: boolean;
  onResponsesUpdate: () => Promise<void>;
}> = ({ question, response, assessment, defaultValue, onTextChange, canScore, onResponsesUpdate }) => {
  const isReadOnly = assessment?.status === 'IN_REVIEW';
  const saveTimeoutRef = useRef<any>(null);

  return (
    <div className="space-y-4">
      <textarea
        name={`question_${question.id}`}
        defaultValue={defaultValue}
        onChange={(e) => {
          if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
          }
          saveTimeoutRef.current = setTimeout(() => {
            onTextChange(e.target.value);
          }, 1000);
        }}
        onBlur={(e) => {
          if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
          }
          onTextChange(e.target.value);
        }}
        rows={4}
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100 disabled:text-gray-500"
        placeholder={isReadOnly ? "Response locked (In Review)" : "Enter your response..."}
        disabled={isReadOnly}
      />

      {/* Score Slider - requires assessments:score permission and notes eligibility */}
      {canScore && response && (
        <ManualScoreSlider
          question={question}
          response={response}
          assessment={assessment}
          onResponsesUpdate={onResponsesUpdate}
        />
      )}
    </div>
  );
};

interface QuestionNodeProps {
  question: Question;
  allQuestions: Question[];
  getResponse: (id: string | number) => Response | undefined;
  renderInput: (question: Question) => React.ReactNode;
  isQuestionAnswered: (id: string) => boolean;
  onSaveResponse: (questionId: string | number, answerValue: string, notes?: string, needsReview?: boolean) => Promise<void>;
  onResetResponseClick: (questionId: number, responseId: number, hasNotes: boolean) => void;
  collapsedQuestions: Set<number>;
  toggleQuestion: (id: number) => void;
  densityClasses: any;
  depth?: number;
  onNext: (currentQuestionId: number) => void;
  canScore: boolean;
  onToggleFlag: (responseId: number, flag: 'needsReview' | 'flaggedForChat', value: boolean) => Promise<void>;
  isReadOnly?: boolean;
}

const QuestionNode: React.FC<QuestionNodeProps> = ({
  question,
  allQuestions,
  getResponse,
  renderInput,
  isQuestionAnswered,
  onSaveResponse,
  onResetResponseClick,
  collapsedQuestions,
  toggleQuestion,
  densityClasses,
  depth = 0,
  onNext,
  canScore,
  onToggleFlag,
  isReadOnly = false
}) => {
  const isQuestionCollapsed = collapsedQuestions.has(question.id);
  const isAnswered = isQuestionAnswered(String(question.id));
  const response = getResponse(question.id);
  const saveTimeoutRef = useRef<any>(null);

  // Robustly find children to render
  // We look for branches where THIS question is the parent

  // Source 1: question.parentBranches (active branches from this parent to its children)
  // This is the standard "outgoing" list from Parent -> Child
  const myParentBranches = question.parentBranches || [];

  const currentAnswer = response?.answerValue;

  const visibleChildrenIds = new Set<number>();

  if (currentAnswer) {
    // Check my outgoing branches (standard path)
    myParentBranches.forEach(branch => {
      if (!branch.isActive) return;

      const required = branch.triggeringOption?.optionValue || branch.parentOptionValue;

      let isMatch = false;
      if (question.questionType === 'multiselect') {
        const optionsList = getQuestionOptionsList(question);
        const selected = getSelectedMultiselectValues(currentAnswer, optionsList);
        isMatch = selected.includes(required);
      } else {
        isMatch = currentAnswer === required;
      }

      if (isMatch) {
        visibleChildrenIds.add(branch.childQuestionId);
      }
    });

    // Source 2: relationships found by scanning all questions (backward link)
    // We check other questions to see if they claim THIS question as their parent
    // via THEIR childBranches.
    allQuestions.forEach(q => {
      if (visibleChildrenIds.has(q.id)) return; // already found
      if (q.id === question.id) return; // skip self

      // Does q have a childBranch pointing to me?
      const branchToMe = q.childBranches?.find(b => b.parentQuestionId === question.id && b.isActive);

      if (branchToMe) {
        const required = branchToMe.triggeringOption?.optionValue || branchToMe.parentOptionValue;
        let isMatch = false;
        // Note: triggering logic depends on Parent (this question) type/answer
        if (question.questionType === 'multiselect') {
          const optionsList = getQuestionOptionsList(question);
          const selected = getSelectedMultiselectValues(currentAnswer, optionsList);
          isMatch = selected.includes(required);
        } else {
          isMatch = currentAnswer === required;
        }

        if (isMatch) {
          visibleChildrenIds.add(q.id);
        }
      }
    });
  }

  const childQuestions = Array.from(visibleChildrenIds)
    .map(id => allQuestions.find(q => q.id === id))
    .filter((q): q is Question => !!q)
    .sort((a, b) => ((a as any).displayOrder || 0) - ((b as any).displayOrder || 0));

  return (
    <div
      id={`question-${question.id}`}
      className={`space-y-3 ${depth > 0 ? 'mt-3 pl-4 border-l-2 border-primary-100' : ''}`}
      style={{ marginLeft: depth > 0 ? '0.5rem' : '0' }}
    >
      <div className={`border border-gray-200 rounded-md bg-white ${depth > 0 ? 'shadow-sm' : ''}`}>
        {/* Question Header */}
        <div
          className={`px-3 py-2 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors ${isAnswered ? 'border-l-4 border-green-500' : 'border-l-4 border-gray-300'}`}
          onClick={() => toggleQuestion(question.id)}
        >
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <div className="flex-shrink-0 mt-0.5">
                {isQuestionCollapsed ? (
                  <ChevronDownIcon className="w-4 h-4 text-gray-600" />
                ) : (
                  <ChevronUpIcon className="w-4 h-4 text-gray-600" />
                )}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {depth > 0 && <span className="text-gray-400 mr-1">↳</span>}
                {isAnswered ? (
                  <CheckCircleIcon className="w-6 h-6 text-green-500 flex-shrink-0" />
                ) : (
                  <XCircleIcon className="w-6 h-6 text-gray-400 flex-shrink-0" />
                )}
                {/* Visual Flag Indicators */}
                {response?.needsReview && (
                  <FlagIcon className="w-5 h-5 text-yellow-500 fill-current flex-shrink-0 animate-pulse" title="Needs Review" />
                )}
                {response?.flaggedForChat && (
                  <ChatBubbleLeftRightIcon className="w-5 h-5 text-blue-500 fill-current flex-shrink-0 animate-pulse" title="Flagged for Chat" />
                )}
              </div>
              <p
                className={`text-sm text-gray-700 flex-1 min-w-0 ${densityClasses.text} ${isQuestionCollapsed
                  ? 'truncate whitespace-nowrap'
                  : 'whitespace-normal break-words'
                  }`}
              >
                {question.questionText}
              </p>
            </div>
          </div>
        </div>

        {/* Question Content */}
        {!isQuestionCollapsed && (
          <div className="p-3 space-y-3">
            {/* Question Tags */}
            {question.tags && question.tags.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {question.tags.map(tag => (
                  <span
                    key={tag}
                    className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}

            {/* Question Input */}
            <div>
              {renderInput(question)}
            </div>

            {/* Notes Field */}
            <div>
              <label className={`block text-xs font-medium text-gray-700 mb-1 ${densityClasses.text}`}>
                Additional Notes (Optional)
              </label>
              <textarea
                name={`notes_${question.id}`}
                defaultValue={getResponse(question.id)?.notes || ''}
                disabled={isReadOnly}
                onChange={(e) => {
                  const notesVal = e.target.value;
                  if (saveTimeoutRef.current) {
                    clearTimeout(saveTimeoutRef.current);
                  }
                  saveTimeoutRef.current = setTimeout(() => {
                    const currentAnswerVal = getResponse(question.id)?.answerValue || '';
                    onSaveResponse(question.id, currentAnswerVal, notesVal);
                  }, 1000);
                }}
                onBlur={(e) => {
                  if (saveTimeoutRef.current) {
                    clearTimeout(saveTimeoutRef.current);
                  }
                  const currentAnswerVal = getResponse(question.id)?.answerValue || '';
                  onSaveResponse(question.id, currentAnswerVal, e.target.value);
                }}
                rows={2}
                className={`w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed ${densityClasses.input}`}
                placeholder={
                  isReadOnly
                    ? "Notes are read-only"
                    : "Add any additional notes..."
                }
              />
            </div>

            {/* Advisor Flag Toggles */}
            {canScore && response && (
              <div className="flex items-center space-x-3 p-2 bg-gray-50 rounded-lg border border-gray-200">
                <button
                  type="button"
                  onClick={() => onToggleFlag(response.id, 'needsReview', !response.needsReview)}
                  className={`flex items-center space-x-1 px-2 py-1 rounded text-xs font-medium transition-colors ${response.needsReview
                      ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                >
                  <FlagIcon className="h-3.5 w-3.5" />
                  <span>{response.needsReview ? 'Needs Review' : 'Mark for Review'}</span>
                </button>
                <button
                  type="button"
                  onClick={() => onToggleFlag(response.id, 'flaggedForChat', !response.flaggedForChat)}
                  className={`flex items-center space-x-1 px-2 py-1 rounded text-xs font-medium transition-colors ${response.flaggedForChat
                      ? 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                >
                  <ChatBubbleLeftRightIcon className="h-3.5 w-3.5" />
                  <span>{response.flaggedForChat ? 'Flagged for Chat' : 'Flag for Chat'}</span>
                </button>
              </div>
            )}

            {/* Actions: Clear Answer & Next */}
            <div className="flex justify-between items-center pt-2">
              {isAnswered ? (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault(); // Prevent form submission
                    e.stopPropagation();
                    const r = getResponse(question.id);
                    if (r) {
                      const hasNotes = !!r.notes && r.notes.trim() !== '';
                      onResetResponseClick(question.id, r.id, hasNotes);
                    }
                  }}
                  className="text-xs text-red-600 hover:text-red-800 hover:underline"
                >
                  Reset Response
                </button>
              ) : <div></div>}

              <button
                type="button"
                onClick={() => onNext(question.id)}
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Next Question
                <ChevronDownIcon className="ml-1.5 h-3 w-3" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Recursive Children */}
      {childQuestions.length > 0 && !isQuestionCollapsed && (
        <div className="mt-2">
          {childQuestions.map(childQ => (
            <QuestionNode
              key={childQ.id}
              question={childQ}
              allQuestions={allQuestions}
              getResponse={getResponse}
              renderInput={renderInput}
              isQuestionAnswered={isQuestionAnswered}
              onSaveResponse={onSaveResponse}
              onResetResponseClick={onResetResponseClick}
              collapsedQuestions={collapsedQuestions}
              toggleQuestion={toggleQuestion}
              densityClasses={densityClasses}
              onNext={onNext}
              depth={depth + 1}
              canScore={canScore}
              onToggleFlag={onToggleFlag}
              isReadOnly={isReadOnly}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const SimpleQuestionnaire: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const assessmentId = parseInt(id || '0');
  const { density } = useLayout();
  const densityClasses = getDensityClasses(density);
  const { user } = useAuth();
  const { setTitle } = useHeader();

  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [showCompletionWarningModal, setShowCompletionWarningModal] = useState(false);
  const [completionWarningData, setCompletionWarningData] = useState({ reviewCount: 0, chatCount: 0 });
  const [resetConfirmData, setResetConfirmData] = useState<{
    questionId: number;
    responseId: number;
    hasNotes: boolean;
  } | null>(null);
  const [deleteNotesCheckbox, setDeleteNotesCheckbox] = useState(false);

  const handleResetResponseClick = (questionId: number, responseId: number, hasNotes: boolean) => {
    setResetConfirmData({ questionId, responseId, hasNotes });
    setDeleteNotesCheckbox(false);
  };

  // Check permissions - use Firebase custom claims instead of role checks
  // Note: Client users can answer questions but cannot score them
  const canScoreTextQuestions = !!(user?.permissions?.includes('assessments:score') ||
    user?.permissions?.includes('admin:full'));

  const canChangeStatus = (user?.role as string) === 'ADVISOR' || user?.role === 'advisor' ||
    (user?.role as string) === 'SUPER_ADMIN' || user?.role === 'super_admin' ||
    (user?.role as string) === 'ADMIN' || user?.role === 'admin' ||
    !!user?.permissions?.includes('assessments:write');

  // Simple state - no complex context
  const [assessment, setAssessment] = useState<Assessment | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [responses, setResponses] = useState<Response[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const responsesRef = useRef(responses);
  responsesRef.current = responses;
  const saveTimeoutsRef = useRef<Record<number, any>>({});
  const [goals, setGoals] = useState<Array<{ id: string, text: string, priority: 'high' | 'medium' | 'low' }>>([]);
  const [showDelegationModal, setShowDelegationModal] = useState(false);
  const [collapsedDimensions, setCollapsedDimensions] = useState<Set<string>>(new Set());
  const [collapsedQuestions, setCollapsedQuestions] = useState<Set<number>>(new Set());

  // Advisor Flag Toggle Handler
  const handleToggleFlag = async (responseId: number, flag: 'needsReview' | 'flaggedForChat', value: boolean) => {
    if (!assessment) return;
    try {
      const updated = await assessmentService.toggleResponseFlag(assessment.id, responseId, flag, value);
      setResponses(prev => prev.map(r => r.id === responseId ? updated : r));
      toast.success(flag === 'needsReview' ? 'Review status updated' : 'Chat status updated');
    } catch (error: any) {
      console.error('Failed to update flag:', error);
      toast.error('Failed to update flag status');
    }
  };

  // Status Helpers
  const handleStatusUpdate = async (newStatus: string) => {
    if (!assessment) return;

    // Check if assessment is fully completed
    const visibleQuestions = getFlattenedVisibleQuestions();
    const totalRequired = visibleQuestions.length;
    const completedRequired = visibleQuestions.filter(q => isQuestionAnswered(String(q.id))).length;
    const isFullyCompleted = totalRequired > 0 && totalRequired === completedRequired;

    if (newStatus === 'COMPLETED' && !isFullyCompleted) {
      toast.error('Cannot mark as completed until all required questions are answered.');
      return;
    }

    if (newStatus === 'COMPLETED') {
      const reviewCount = responses.filter(r => r.needsReview).length;
      const chatCount = responses.filter(r => r.flaggedForChat).length;
      if (reviewCount > 0 || chatCount > 0) {
        setCompletionWarningData({ reviewCount, chatCount });
        setShowCompletionWarningModal(true);
        return;
      }
    }

    setIsUpdatingStatus(true);
    try {
      const updatedDiff = await assessmentService.updateAssessment(assessment.id, {
        status: newStatus as any
      });
      setAssessment(prev => prev ? ({ ...prev, status: updatedDiff.status }) : null);
      toast.success(`Status updated to ${getStatusText(newStatus)}`);
    } catch (error: any) {
      toast.error(error.message || 'Failed to update status');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const confirmStatusUpdateToCompleted = async () => {
    setShowCompletionWarningModal(false);
    setIsUpdatingStatus(true);
    try {
      const updatedDiff = await assessmentService.updateAssessment(assessmentId, {
        status: 'COMPLETED'
      });
      setAssessment(prev => prev ? ({ ...prev, status: updatedDiff.status }) : null);
      toast.success('Status updated to Completed');
    } catch (error: any) {
      toast.error(error.message || 'Failed to update status');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'IN_PROGRESS':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'IN_REVIEW':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'NEW':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'ARCHIVED':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return 'Completed';
      case 'IN_PROGRESS':
        return 'In Progress';
      case 'IN_REVIEW':
        return 'In Review';
      case 'NEW':
        return 'New';
      case 'ARCHIVED':
        return 'Archived';
      default:
        return status || 'Unknown';
    }
  };

  // Load data once on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        console.log('Loading questionnaire data...');

        // Load all data in parallel - simple and straightforward
        const [assessmentData, responsesData] = await Promise.all([
          assessmentService.getAssessment(assessmentId),
          assessmentService.getAssessmentResponses(assessmentId)
        ]);

        // Fetch questions - filter by questionnaire label & version from assessment
        const questionsData = await questionService.getQuestions({
          questionnaireLabel: assessmentData.questionnaireLabel || undefined,
          questionnaireVersion: assessmentData.questionnaireVersion || undefined
        });

        setAssessment(assessmentData);
        setQuestions(questionsData);
        setResponses(responsesData);

        // Collapse all questions by default
        const allQuestionIds = new Set(questionsData.map(q => q.id));
        setCollapsedQuestions(allQuestionIds);

        if (assessmentData?.client) {
          setTitle(`${assessmentData.client.name} - ${assessmentData.questionnaire?.name || 'Evaluation'}`);
        }

        // Load goals from assessment
        if (assessmentData?.goals) {
          // Convert goals object to array format
          const goalsArray = Array.isArray(assessmentData.goals)
            ? assessmentData.goals
            : Object.entries(assessmentData.goals).map(([key, value], index) => ({
              id: `goal-${index}`,
              text: typeof value === 'string' ? value : key,
              priority: 'medium' as const
            }));
          setGoals(goalsArray);
        }

        console.log('Loaded:', {
          assessment: assessmentData.id,
          questions: questionsData.length,
          responses: responsesData.length,
          goals: assessmentData?.goals?.length || 0
        });
      } catch (error: any) {
        console.error('Failed to load questionnaire:', error);
        toast.error('Failed to load questionnaire');
      } finally {
        setIsLoading(false);
      }
    };

    if (assessmentId) {
      loadData();
    }
  }, [assessmentId]);

  // Get response for a question
  const getResponse = useCallback((questionId: string | number): Response | undefined => {
    return responsesRef.current.find(r =>
      r.questionId === Number(questionId) || (r.question && r.question.id === Number(questionId))
    );
  }, []);

  // Check if question is answered
  const isQuestionAnswered = (questionId: string): boolean => {
    const response = getResponse(questionId);
    return response !== undefined && response.answerValue !== '' && response.answerValue !== null;
  };

  // Toggle dimension collapse
  const toggleDimension = (dimensionName: string) => {
    const newCollapsed = new Set(collapsedDimensions);
    if (newCollapsed.has(dimensionName)) {
      newCollapsed.delete(dimensionName);
    } else {
      newCollapsed.add(dimensionName);
    }
    setCollapsedDimensions(newCollapsed);
  };

  // Toggle question collapse
  const toggleQuestion = (questionId: number, forceState?: boolean) => {
    const newCollapsed = new Set(collapsedQuestions);
    const shouldExpand = forceState !== undefined ? forceState : newCollapsed.has(questionId);

    if (!shouldExpand) {
      newCollapsed.add(questionId);
    } else {
      newCollapsed.delete(questionId);
    }
    setCollapsedQuestions(newCollapsed);
  };



  // Get flattened list of currently visible questions in display order
  const getFlattenedVisibleQuestions = (): Question[] => {
    if (!questions.length) return [];

    const result: Question[] = [];

    // Helper to find children exactly like QuestionNode does
    const getVisibleChildren = (parent: Question): Question[] => {
      const response = getResponse(parent.id);
      const currentAnswer = response?.answerValue;
      if (!currentAnswer) return [];

      const childrenIds = new Set<number>();

      // 1. From branches pointing from this parent (outgoing)
      (parent.parentBranches || []).forEach(branch => {
        if (!branch.isActive) return;
        const required = branch.triggeringOption?.optionValue || branch.parentOptionValue;
        let isMatch = false;
        if (parent.questionType === 'multiselect') {
          const optionsList = getQuestionOptionsList(parent);
          const selected = getSelectedMultiselectValues(currentAnswer, optionsList);
          isMatch = selected.includes(required);
        } else {
          isMatch = currentAnswer === required;
        }
        if (isMatch) childrenIds.add(branch.childQuestionId);
      });

      // 2. From all questions scanning their incoming branches pointing to this parent
      questions.forEach(q => {
        if (childrenIds.has(q.id) || q.id === parent.id) return;
        const branchToMe = q.childBranches?.find(b => b.parentQuestionId === parent.id && b.isActive);
        if (branchToMe) {
          const required = branchToMe.triggeringOption?.optionValue || branchToMe.parentOptionValue;
          let isMatch = false;
          if (parent.questionType === 'multiselect') {
            const optionsList = getQuestionOptionsList(parent);
            const selected = getSelectedMultiselectValues(currentAnswer, optionsList);
            isMatch = selected.includes(required);
          } else {
            isMatch = currentAnswer === required;
          }
          if (isMatch) childrenIds.add(q.id);
        }
      });

      return Array.from(childrenIds)
        .map(id => questions.find(q => q.id === id))
        .filter((q): q is Question => !!q)
        .sort((a, b) => ((a as any).displayOrder || 0) - ((b as any).displayOrder || 0));
    };

    const addRecursive = (q: Question) => {
      result.push(q);
      getVisibleChildren(q).forEach(child => addRecursive(child));
    };

    // Group by dimension to maintain correct sequence
    const byDim = questions.reduce((acc, q) => {
      const dimName = q.dimension?.label || q.dimension?.name || 'Other';
      if (!acc[dimName]) acc[dimName] = [];
      acc[dimName].push(q);
      return acc;
    }, {} as Record<string, Question[]>);

    // Sort dimensions by displayOrder
    const sortedDimNames = Object.keys(byDim).sort((a, b) => {
      const dA = byDim[a][0]?.dimension?.displayOrder ?? 999;
      const dB = byDim[b][0]?.dimension?.displayOrder ?? 999;
      return dA - dB;
    });

    sortedDimNames.forEach(dimName => {
      const dimQuestions = byDim[dimName];
      // Roots match UI loop filter logic exactly
      const roots = dimQuestions
        .filter(q => !q.isChild && (!q.childBranches || q.childBranches.length === 0))
        .sort((a, b) => ((a as any).displayOrder || 0) - ((b as any).displayOrder || 0));

      roots.forEach(root => addRecursive(root));
    });

    return result;
  };

  // Focus a specific question
  const focusQuestion = (questionId: number) => {
    // Expand dimension
    const q = questions.find(fq => fq.id === questionId);
    if (q) {
      const dimName = q.dimension?.label || q.dimension?.name || 'Other';
      setCollapsedDimensions(prev => {
        const next = new Set(prev);
        next.delete(dimName);
        return next;
      });
    }

    // Expand question (remove from collapsed set)
    toggleQuestion(questionId, true); // force expand

    // Scroll and focus
    setTimeout(() => {
      const el = document.getElementById(`question-${questionId}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Focus the first interactive input or textarea inside this question card
        const firstInput = el.querySelector('input, textarea, select') as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null;
        if (firstInput) {
          firstInput.focus();
        }
      }
    }, 100);
  };

  const handleDimensionClick = (_dimensionId: number, dimensionName: string) => {
    // Get visible questions in this dimension
    const visibleQs = getFlattenedVisibleQuestions();
    const dimQs = visibleQs.filter(q => {
      const qDimName = q.dimension?.label || q.dimension?.name || 'Other';
      return qDimName === dimensionName;
    });

    // Find the first unanswered question
    const firstUnanswered = dimQs.find(q => !isQuestionAnswered(String(q.id)));

    if (firstUnanswered) {
      focusQuestion(firstUnanswered.id);
    } else if (dimQs.length > 0) {
      // If all answered, scroll to the first question of this dimension
      focusQuestion(dimQs[0].id);
      toast.success(`All required questions in ${dimensionName} are answered.`);
    }
  };

  const handleNext = (currentQuestionId?: number) => {
    const visible = getFlattenedVisibleQuestions();

    if (!currentQuestionId) {
      // Initial load: Find first unanswered or first question
      const firstUnanswered = visible.find(q => !isQuestionAnswered(String(q.id)));
      if (firstUnanswered) {
        focusQuestion(firstUnanswered.id);
      } else if (visible.length > 0) {
        focusQuestion(visible[0].id);
      }
      return;
    }

    // Find next after current
    const currentIndex = visible.findIndex(q => q.id === currentQuestionId);
    if (currentIndex === -1) return;

    // Go to next question in sequence, regardless of answer status
    if (currentIndex + 1 < visible.length) {
      focusQuestion(visible[currentIndex + 1].id);
    } else {
      toast.success('End of evaluation reached');
    }
  };

  // Initial focus on load
  useEffect(() => {
    if (!isLoading && questions.length > 0) {
      // Allow a slight delay for render
      setTimeout(() => {
        handleNext();
      }, 500);
    }
  }, [isLoading, questions.length]);

  // Save goals to backend
  const saveGoals = async (newGoals: Array<{ id: string, text: string, priority: 'high' | 'medium' | 'low' }>) => {
    try {
      await assessmentService.updateAssessmentGoals(assessmentId, newGoals);
      setGoals(newGoals);
      toast.success('Notes saved successfully');
    } catch (error: any) {
      console.error('Failed to save notes:', error);
      toast.error('Failed to save notes');
    }
  };

  // Save response
  const saveResponse = async (questionId: string | number, answerValue: string, notes?: string, needsReview?: boolean) => {
    try {
      setIsSaving(true);

      // Auto-advance status if NEW
      if (assessment?.status === 'NEW') {
        const updated = await assessmentService.updateAssessment(assessmentId, { status: 'IN_PROGRESS' });
        setAssessment(prev => prev ? ({ ...prev, status: updated.status }) : null);
      }

      const response = await assessmentService.saveResponse(assessmentId, {
        questionId: String(questionId),
        answerValue,
        notes,
        needsReview
      });

      // Update local state
      setResponses(prev => {
        const existingIndex = prev.findIndex(r =>
          r.questionId === Number(questionId) || (r.question && r.question.id === Number(questionId))
        );
        if (existingIndex >= 0) {
          const updated = [...prev];
          updated[existingIndex] = response;
          return updated;
        } else {
          return [...prev, response];
        }
      });

      toast.success('Response saved');
      setIsDirty(true);
    } catch (error: any) {
      toast.error('Failed to save response');
    } finally {
      setIsSaving(false);
    }
  };

  // Delete response (un-answer a question)
  const deleteResponse = async (responseId: number, deleteNotes?: boolean) => {
    try {
      setIsSaving(true);
      await assessmentService.deleteResponse(assessmentId, responseId);

      // Remove from local state
      setResponses(prev => prev.filter(r => r.id !== responseId));

      toast.success(deleteNotes ? 'Response and notes reset' : 'Answer cleared');
    } catch (error: any) {
      toast.error('Failed to delete response');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle form submission
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    // Process all form data
    const formResponses: { questionId: string; answerValue: string; notes?: string }[] = [];

    for (const [key, value] of formData.entries()) {
      if (key.startsWith('question_')) {
        const questionId = key.replace('question_', '');
        const notesKey = `notes_${questionId}`;
        const notes = formData.get(notesKey) as string;

        formResponses.push({
          questionId,
          answerValue: value as string,
          notes: notes || undefined
        });
      }
    }

    // Save all responses
    formResponses.forEach(({ questionId, answerValue, notes }) => {
      saveResponse(questionId, answerValue, notes);
    });
  };

  // Render question based on type
  const renderQuestion = (question: Question) => {
    const response = getResponse(question.id);
    const defaultValue = response?.answerValue || '';
    const isReadOnly = assessment?.status === 'IN_REVIEW';

    // Auto-save handler
    const handleChange = async (value: string) => {
      if (isReadOnly) return;
      await saveResponse(question.id, value, getResponse(question.id)?.notes);
    };

    if (question.questionType === 'text') {
      const textResponse = getResponse(question.id);
      return (
        <TextQuestionWithScore
          question={question}
          response={textResponse}
          assessment={assessment}
          defaultValue={defaultValue}
          onTextChange={handleChange}
          canScore={canScoreTextQuestions}
          onResponsesUpdate={async () => {
            const updatedResponses = await assessmentService.getAssessmentResponses(assessmentId);
            setResponses(updatedResponses);
          }}
        />
      );
    }

    let inputElement: React.ReactNode = null;

    switch (question.questionType) {
      case 'binary':
        inputElement = (
          <div className="space-y-3">
            <div className="flex space-x-4">
              <label className={`flex items-center ${isReadOnly ? 'cursor-not-allowed opacity-60' : ''}`}>
                <input
                  type="radio"
                  name={`question_${question.id}`}
                  value="Yes"
                  checked={defaultValue === 'Yes'}
                  onChange={(e) => e.target.checked && handleChange('Yes')}
                  className="mr-2 disabled:cursor-not-allowed"
                  disabled={isReadOnly}
                />
                Yes
              </label>
              <label className={`flex items-center ${isReadOnly ? 'cursor-not-allowed opacity-60' : ''}`}>
                <input
                  type="radio"
                  name={`question_${question.id}`}
                  value="No"
                  checked={defaultValue === 'No'}
                  onChange={(e) => e.target.checked && handleChange('No')}
                  className="mr-2 disabled:cursor-not-allowed"
                  disabled={isReadOnly}
                />
                No
              </label>
            </div>
          </div>
        );
        break;

      case 'multiple_choice': {
        const multipleChoiceOptions = getQuestionOptionsList(question);
        inputElement = (
          <div className="space-y-3">
            {multipleChoiceOptions.map(option => (
              <label key={option} className={`flex items-center ${isReadOnly ? 'cursor-not-allowed opacity-60' : ''}`}>
                <input
                  type="radio"
                  name={`question_${question.id}`}
                  value={option}
                  checked={defaultValue === option}
                  onChange={(e) => e.target.checked && handleChange(option)}
                  className="mr-2 disabled:cursor-not-allowed"
                  disabled={isReadOnly}
                />
                {option}
              </label>
            ))}
          </div>
        );
        break;
      }

      case 'multiselect': {
        const multiselectOptions = getQuestionOptionsList(question);
        // Parse existing value
        const currentValues = typeof defaultValue === 'string'
          ? getSelectedMultiselectValues(defaultValue, multiselectOptions)
          : Array.isArray(defaultValue) ? defaultValue : [];

        const handleMultiselectChange = (option: string, checked: boolean) => {
          if (isReadOnly) return;
          const newValues = checked
            ? [...currentValues, option]
            : currentValues.filter(v => v !== option);
          handleChange(newValues.join(','));
        };

        inputElement = (
          <div className="space-y-3">
            {multiselectOptions.map(option => (
              <label key={option} className={`flex items-center ${isReadOnly ? 'cursor-not-allowed opacity-60' : ''}`}>
                <input
                  type="checkbox"
                  name={`question_${question.id}`}
                  value={option}
                  checked={currentValues.includes(option)}
                  onChange={(e) => handleMultiselectChange(option, e.target.checked)}
                  className="mr-2 disabled:cursor-not-allowed"
                  disabled={isReadOnly}
                />
                {option}
              </label>
            ))}
          </div>
        );
        break;
      }

      case 'scale': {
        // Use question options (display order) and scoringRules for scale labels
        const scaleOptionsList = getScaleLabels(getQuestionOptionsAsLegacyShape(question) ?? undefined, getScoringRulesMap(question));

        inputElement = (
          <div className="space-y-2">
            {scaleOptionsList.map((option) => {
              const isChecked = getMatchingScaleOption(defaultValue, scaleOptionsList)?.value === option.value;
              return (
                <label key={option.value} className={`flex items-center space-x-2 p-2 rounded-md transition-colors ${isReadOnly ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:bg-gray-50'}`}>
                  <input
                    type="radio"
                    name={`question_${question.id}`}
                    value={option.value}
                    checked={isChecked}
                    onChange={(e) => e.target.checked && handleChange(option.value)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 flex-shrink-0 disabled:cursor-not-allowed"
                    disabled={isReadOnly}
                  />
                  <span className="text-sm text-gray-700">{option.label}</span>
                </label>
              );
            })}
          </div>
        );
        break;
      }

      default:
        inputElement = (
          <input
            type="text"
            name={`question_${question.id}`}
            defaultValue={defaultValue}
            onChange={(e) => {
              if (isReadOnly) return;
              if (saveTimeoutsRef.current[question.id]) {
                clearTimeout(saveTimeoutsRef.current[question.id]);
              }
              saveTimeoutsRef.current[question.id] = setTimeout(() => {
                handleChange(e.target.value);
              }, 1000);
            }}
            onBlur={(e) => {
              if (isReadOnly) return;
              if (saveTimeoutsRef.current[question.id]) {
                clearTimeout(saveTimeoutsRef.current[question.id]);
              }
              handleChange(e.target.value);
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
            placeholder={isReadOnly ? "Response locked" : "Enter your response..."}
            disabled={isReadOnly}
          />
        );
        break;
    }

    const showSliderForNonText =
      canScoreTextQuestions &&
      response &&
      assessment?.status === 'IN_REVIEW' &&
      response.answerValue !== '' &&
      response.answerValue !== null &&
      getAutomaticScore(question, response.answerValue) === 0;

    return (
      <div className="space-y-4">
        {inputElement}
        {showSliderForNonText && response && (
          <ManualScoreSlider
            question={question}
            response={response}
            assessment={assessment}
            onResponsesUpdate={async () => {
              const updatedResponses = await assessmentService.getAssessmentResponses(assessmentId);
              setResponses(updatedResponses);
            }}
          />
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!assessment) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900">Evaluation not found</h3>
        <button
          onClick={() => navigate(`/assessments/${assessmentId}`)}
          className="mt-4 btn btn-primary"
        >
          Back to Evaluations
        </button>
      </div>
    );
  }

  // Group questions by dimension
  const questionsByDimension = questions.reduce((acc, question) => {
    const dimensionName = question.dimension?.label || question.dimension?.name || 'Other';
    if (!acc[dimensionName]) {
      acc[dimensionName] = [];
    }
    acc[dimensionName].push(question);
    return acc;
  }, {} as Record<string, Question[]>);

  // Calculate progress on visible/required questions only
  const visibleQuestions = getFlattenedVisibleQuestions();
  const totalQuestions = visibleQuestions.length;
  const completedQuestions = visibleQuestions.filter(q => isQuestionAnswered(String(q.id))).length;

  const visibleQuestionsByDimension = visibleQuestions.reduce((acc, q) => {
    const dimName = q.dimension?.label || q.dimension?.name || 'Other';
    if (!acc[dimName]) acc[dimName] = [];
    acc[dimName].push(q);
    return acc;
  }, {} as Record<string, Question[]>);

  // Create dimensions data for overview, sorted by displayOrder
  const dimensions = Object.entries(questionsByDimension)
    .map(([name, dimQs]) => {
      const visibleQsInDim = visibleQuestionsByDimension[name] || [];
      const completedQsInDim = visibleQsInDim.filter(q => isQuestionAnswered(String(q.id))).length;
      return {
        id: dimQs[0]?.dimension?.id || 0,
        name,
        displayOrder: dimQs[0]?.dimension?.displayOrder ?? 999,
        description: dimQs[0]?.dimension?.description || '',
        questionCount: visibleQsInDim.length,
        completedCount: completedQsInDim
      };
    })
    .sort((a, b) => a.displayOrder - b.displayOrder);

  return (
    <QuestionnaireErrorBoundary>
      <div className="max-w-6xl mx-auto pb-40 sm:pb-40">
        {/* Header */}
        <div className={`flex items-center justify-between mb-6 ${densityClasses.spacing}`}>
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate(`/assessments/${assessmentId}`)}
              className="text-gray-400 hover:text-gray-600"
            >
              <ArrowLeftIcon className="h-6 w-6" />
            </button>
            <div>
              <h1 className={`font-bold text-gray-900 ${densityClasses.heading}`}>
                {assessment.questionnaire?.name || 'Evaluation'}
              </h1>
              <p className={`text-gray-500 ${densityClasses.text}`}>
                {assessment.client?.name} • Evaluation #{assessment.id}
              </p>
            </div>
          </div>
          {/*           <button
            onClick={() => setShowDelegationModal(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            <UserGroupIcon className="h-5 w-5" />
            <span>Delegate Questions</span>
          </button> */}
        </div>

        {/* QuestionnaireOverview moved to bottom sticky footer */}

        {/* Client Goals Section */}
        <div className="mb-6">
          <ClientGoalsSection
            goals={goals}
            onGoalsChange={saveGoals}
            isEditable={true}
          />
        </div>

        {/* Goal-Driven Scoring Analysis */}

        {/* Questions by Dimension */}
        <form id="questionnaire-form" onSubmit={handleSubmit} className={`space-y-4 ${densityClasses.spacing}`}>
          {dimensions.map((dim) => {
            const dimensionName = dim.name;
            const dimensionQuestions = questionsByDimension[dimensionName];
            const isDimensionCollapsed = collapsedDimensions.has(dimensionName);
            const dimensionAnsweredCount = dimensionQuestions.filter(q => isQuestionAnswered(String(q.id))).length;

            return (
              <div key={dimensionName} className="bg-white rounded-lg shadow-sm border border-gray-200">
                {/* Dimension Header */}
                <div
                  className={`bg-blue-50 px-4 py-3 border-b border-blue-200 cursor-pointer hover:bg-blue-100 transition-colors`}
                  onClick={() => toggleDimension(dimensionName)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3 flex-1">
                      <div className="flex-shrink-0">
                        {isDimensionCollapsed ? (
                          <ChevronDownIcon className="w-5 h-5 text-gray-600" />
                        ) : (
                          <ChevronUpIcon className="w-5 h-5 text-gray-600" />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <h2 className={`font-semibold text-blue-900 ${densityClasses.heading}`}>
                            {dimensionName}
                          </h2>
                          <span className="text-xs text-gray-500">
                            ({dimensionAnsweredCount}/{dimensionQuestions.length})
                          </span>
                          {dimensionAnsweredCount === dimensionQuestions.length && dimensionAnsweredCount > 0 ? (
                            <CheckCircleIcon className="w-6 h-6 text-green-500" />
                          ) : dimensionAnsweredCount > 0 ? (
                            <div className="w-6 h-6 rounded-full border-2 border-yellow-500 bg-yellow-100" />
                          ) : (
                            <XCircleIcon className="w-6 h-6 text-gray-400" />
                          )}
                        </div>
                        {!isDimensionCollapsed && (
                          <p className={`text-gray-600 mt-1 text-sm ${densityClasses.text}`}>
                            {dimensionQuestions[0]?.dimension?.description || ''}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Questions in this dimension */}
                {!isDimensionCollapsed && (
                  <div className={`p-3 ${densityClasses.card}`}>
                    <div className={`space-y-3 ${densityClasses.spacing}`}>
                      {dimensionQuestions
                        .filter(q => !q.isChild && (!q.childBranches || q.childBranches.length === 0))
                        .sort((a, b) => ((a as any).displayOrder || 0) - ((b as any).displayOrder || 0))
                        .map((question) => (
                          <QuestionNode
                            key={question.id}
                            question={question}
                            allQuestions={questions}
                            getResponse={(id: string | number) => getResponse(id)}
                            renderInput={renderQuestion}
                            isQuestionAnswered={(id: string) => isQuestionAnswered(id)}
                            onSaveResponse={saveResponse}
                            onResetResponseClick={handleResetResponseClick}
                            collapsedQuestions={collapsedQuestions}
                            toggleQuestion={toggleQuestion}
                            densityClasses={densityClasses}
                            onNext={handleNext}
                            canScore={canScoreTextQuestions}
                            onToggleFlag={handleToggleFlag}
                            isReadOnly={assessment?.status === 'IN_REVIEW' || assessment?.status === 'COMPLETED' || assessment?.status === 'ARCHIVED'}
                          />
                        ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}

        </form>

        {/* Sticky Footer */}
        <div className="fixed bottom-0 left-0 md:left-64 right-0 bg-white border-t border-gray-200 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)] z-50 px-4 py-4">
          <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
            {/* Left: Progress */}
            <div className="w-full sm:w-auto">
              <QuestionnaireOverview
                dimensions={dimensions}
                totalQuestions={totalQuestions}
                completedQuestions={completedQuestions}
                compact={true}
                onDimensionClick={handleDimensionClick}
              />
            </div>

            {/* Right: Actions */}
            <div className="flex items-center space-x-3 w-full sm:w-auto justify-end flex-wrap gap-y-2">

              {/* Status Change Widget */}
              <div className="relative">
                {!canChangeStatus ? (
                  <div className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(assessment.status)}`}>
                    {getStatusText(assessment.status)}
                  </div>
                ) : (
                  <Menu as="div" className="relative inline-block text-left">
                    <Menu.Button
                      disabled={isUpdatingStatus}
                      className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${getStatusColor(assessment.status)} ${isUpdatingStatus ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:opacity-80'}`}
                    >
                      {getStatusText(assessment.status)}
                      <ChevronDownIcon className="ml-1 h-3 w-3" aria-hidden="true" />
                    </Menu.Button>

                    <Transition
                      as={React.Fragment}
                      enter="transition ease-out duration-100"
                      enterFrom="transform opacity-0 scale-95"
                      enterTo="transform opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                    >
                      <Menu.Items className="origin-bottom-right absolute right-0 bottom-full mb-2 w-40 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
                        <div className="py-1">
                          {/* IN_PROGRESS is always an option to move to, unless we are already there. 
                              However, if IN_REVIEW, only Advisors+ can move back to IN_PROGRESS. */}
                          {(!['IN_REVIEW'].includes(assessment.status) ||
                            (user?.role && ['advisor', 'ADVISOR', 'admin', 'ADMIN', 'super_admin', 'SUPER_ADMIN'].includes(user.role))) && (
                              <Menu.Item>
                                {({ active }) => (
                                  <button
                                    type="button"
                                    onClick={() => handleStatusUpdate('IN_PROGRESS')}
                                    className={`${active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'} block px-4 py-2 text-sm w-full text-left`}
                                  >
                                    In Progress
                                  </button>
                                )}
                              </Menu.Item>
                            )}

                          {/* IN_REVIEW option */}
                          <Menu.Item>
                            {({ active }) => (
                              <button
                                type="button"
                                onClick={() => handleStatusUpdate('IN_REVIEW')}
                                className={`${active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'} block px-4 py-2 text-sm w-full text-left`}
                              >
                                In Review
                              </button>
                            )}
                          </Menu.Item>

                          <Menu.Item>
                            {({ active }) => {
                              const isCompletedDisabled = totalQuestions > 0 && completedQuestions < totalQuestions;
                              return (
                                <div className={`flex items-center justify-between w-full px-4 py-2 text-sm ${active ? 'bg-gray-100' : ''}`}>
                                  <button
                                    type="button"
                                    onClick={() => handleStatusUpdate('COMPLETED')}
                                    disabled={isCompletedDisabled}
                                    className={`flex-1 text-left font-medium ${isCompletedDisabled ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700'}`}
                                  >
                                    Completed
                                  </button>
                                  {isCompletedDisabled && (
                                    <div className="relative group ml-2 flex-shrink-0">
                                      <InformationCircleIcon
                                        className="h-4 w-4 text-blue-500 cursor-pointer hover:text-blue-600 transition-colors"
                                      />
                                      {/* Premium absolute tooltip styled nicely */}
                                      <div className="absolute right-0 bottom-full mb-2 hidden group-hover:flex flex-col w-52 bg-slate-900 text-slate-100 text-[11px] rounded-lg p-2.5 shadow-xl z-[9999] border border-slate-700 pointer-events-none transition-all duration-200 ease-out leading-relaxed">
                                        <span className="font-bold text-blue-400 mb-1">Unanswered Required Questions</span>
                                        <span>Expand the progress tracker in the center of the footer to identify and navigate to remaining questions.</span>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            }}
                          </Menu.Item>

                          {/* ARCHIVED only for Admins */}
                          {(user?.role && ['admin', 'ADMIN', 'super_admin', 'SUPER_ADMIN'].includes(user.role)) && (
                            <Menu.Item>
                              {({ active }) => (
                                <button
                                  type="button"
                                  onClick={() => handleStatusUpdate('ARCHIVED')}
                                  className={`${active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                                    } block px-4 py-2 text-sm w-full text-left`}
                                >
                                  Archived
                                </button>
                              )}
                            </Menu.Item>
                          )}
                        </div>
                      </Menu.Items>
                    </Transition>
                  </Menu>
                )}
              </div>

              {/* View Analysis */}
              <Link
                to={`/assessments/${assessment.id}/maturity`}
                className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors duration-200"
              >
                <DocumentTextIcon className="w-4 h-4 mr-2" />
                View Analysis
              </Link>

              {/* Cancel Button */}
              <button
                type="button"
                disabled={isSaving}
                onClick={() => navigate(`/assessments/${assessmentId}`)}
                className="h-10 px-4 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 bg-white shadow-sm font-medium text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
              >
                Cancel
              </button>

              {/* Save Assessment Button - Only Visible if isDirty */}
              {isDirty && (
                <button
                  type="button"
                  onClick={() => navigate(`/assessments/${assessmentId}`)}
                  className="h-10 px-4 bg-primary-600 text-white rounded-md hover:bg-primary-700 shadow-sm font-medium text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Save Evaluation
                </button>
              )}
            </div>
          </div>
        </div>

        {/* FloatingProgressBar removed as per request */}

        {/* Mobile Progress Bar - Bottom Fixed */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 sm:hidden touch-manipulation">
          <div className="flex items-center justify-between mb-2">
            <span className={`font-medium text-gray-900 ${densityClasses.text}`}>
              Progress: {completedQuestions}/{totalQuestions}
            </span>
            <span className={`text-sm text-gray-500 ${densityClasses.text}`}>
              {Math.round((completedQuestions / totalQuestions) * 100)}%
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(completedQuestions / totalQuestions) * 100}%` }}
            />
          </div>
        </div>

        {/* Delegation Modal */}
        {showDelegationModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-10 mx-auto p-5 border w-full max-w-3xl shadow-lg rounded-md bg-white">
              <QuestionDelegation
                questions={questions}
                assessment={assessment}
                onQuestionsDelegated={() => {
                  toast.success('Questions delegated successfully');
                  setShowDelegationModal(false);
                }}
                onClose={() => setShowDelegationModal(false)}
              />
            </div>
          </div>
        )}

        {/* Completion Warning Modal */}
        {showCompletionWarningModal && (
          <div className="fixed inset-0 z-50 overflow-y-auto font-sans" aria-labelledby="modal-title" role="dialog" aria-modal="true">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div
                className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                aria-hidden="true"
                onClick={() => setShowCompletionWarningModal(false)}
              ></div>

              <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

              <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6 animate-fade-in-up">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 sm:mx-0 sm:h-10 sm:w-10">
                    <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600" aria-hidden="true" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                      Flags Active on Evaluation
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        There are <span className="font-bold text-yellow-600">{completionWarningData.reviewCount}</span> responses flagged for review, and <span className="font-bold text-blue-600">{completionWarningData.chatCount}</span> flagged for conversation.
                      </p>
                      <p className="text-sm text-gray-500 mt-2">
                        Are you sure you want to complete this evaluation?
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse gap-2">
                  <button
                    type="button"
                    onClick={confirmStatusUpdateToCompleted}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-yellow-600 text-base font-medium text-white hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Yes, Complete
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowCompletionWarningModal(false)}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Reset Response Confirmation Modal */}
        {resetConfirmData && (
          <div className="fixed inset-0 z-50 overflow-y-auto font-sans" aria-labelledby="reset-modal-title" role="dialog" aria-modal="true">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div
                className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                aria-hidden="true"
                onClick={() => setResetConfirmData(null)}
              ></div>

              <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

              <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6 animate-fade-in-up">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <ExclamationTriangleIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" id="reset-modal-title">
                      Reset Response
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Are you sure you want to reset the response for this question?
                      </p>
                      {resetConfirmData.hasNotes && (
                        <label className="flex items-center space-x-2 mt-4 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={deleteNotesCheckbox}
                            onChange={(e) => setDeleteNotesCheckbox(e.target.checked)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                          />
                          <span className="text-sm text-gray-700">Do you want to remove the notes from this question?</span>
                        </label>
                      )}
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse gap-2">
                  <button
                    type="button"
                    onClick={async () => {
                      if (!resetConfirmData) return;
                      const { questionId, responseId } = resetConfirmData;
                      setResetConfirmData(null);
                      if (deleteNotesCheckbox) {
                        await deleteResponse(responseId, true);
                      } else {
                        const r = getResponse(questionId);
                        const notesVal = r?.notes || '';
                        await saveResponse(questionId, "", notesVal, true);
                      }
                    }}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Reset Response
                  </button>
                  <button
                    type="button"
                    onClick={() => setResetConfirmData(null)}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </QuestionnaireErrorBoundary>
  );
};

export default SimpleQuestionnaire;
